import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SearchModal from '@/components/ui-custom/SearchModal';
import Chatbot from '@/components/ui-custom/Chatbot';
import Inicio from '@/pages/Inicio';
import Embajador from '@/pages/Embajador';
import Catalogo from '@/pages/Catalogo';
import Tratamientos from '@/pages/Tratamientos';
import type { Producto } from '@/types';
import { useAmbassador } from '@/hooks/useAmbassador';

type Page = 'inicio' | 'embajador' | 'catalogo' | 'tratamientos';

function App() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  
  // El slug es la primera parte de la ruta, si existe
  const slug = pathname.split('/').filter(Boolean)[0];
  const { ambassador, loading, leader } = useAmbassador(slug);

  const [currentPage, setCurrentPage] = useState<Page>('inicio');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [catalogoLinea, setCatalogoLinea] = useState<string | null>(null);
  const [catalogoProducto, setCatalogoProducto] = useState<Producto | null>(null);
  const [tratamientoSeleccionado, setTratamientoSeleccionado] = useState<string | null>(null);

  // Redirección automática al slug del líder si no hay slug en la URL
  useEffect(() => {
    // Si no estamos cargando, tenemos un líder, y el pathname está vacío o es solo '/'
    if (!loading && leader && (!slug || slug === 'index.html')) {
      const hash = window.location.hash;
      const targetSlug = leader.slug;
      
      if (targetSlug && targetSlug !== 'undefined') {
        // Usamos replace para no ensuciar el historial de navegación
        navigate(`/${targetSlug}${hash}`, { replace: true });
      }
    }
  }, [loading, leader, slug, navigate]);

  useEffect(() => {
    // Scroll to top on initial load
    window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);

    // Handle URL hash for navigation
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1) as Page;
      if (['inicio', 'embajador', 'catalogo', 'tratamientos'].includes(hash)) {
        setCurrentPage(hash);
      } else {
        // If no hash, default to home but keep it clean
        setCurrentPage('inicio');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange(); // Check initial hash

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Actualizar el título de la página dinámicamente para SEO
  useEffect(() => {
    const pageTitles: Record<Page, string> = {
      inicio: 'Volando Alto - Bienestar Natural',
      embajador: 'Sé un Embajador Ismerely | Volando Alto',
      catalogo: 'Catálogo de Productos | Volando Alto',
      tratamientos: 'Tratamientos | Volando Alto'
    };
    
    document.title = pageTitles[currentPage] || 'Volando Alto - Bienestar Natural';
  }, [currentPage]);

  const navigateTo = (page: Page, params?: { linea?: string; producto?: Producto; tratamiento?: string }) => {
    setCurrentPage(page);
    setCatalogoLinea(params?.linea || null);
    setCatalogoProducto(params?.producto || null);
    setTratamientoSeleccionado(params?.tratamiento || null);
    window.location.hash = page;
    // Scroll to top unless a specific treatment is requested
    if (!params?.tratamiento) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSelectProduct = (producto: Producto) => {
    navigateTo('catalogo', { linea: producto.linea, producto: producto });
  };
  
  const handleSelectTratamiento = (tratamiento: string) => {
    navigateTo('tratamientos', { tratamiento: tratamiento });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9f5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#8abd31]"></div>
      </div>
    );
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'inicio':
        return <Inicio onNavigate={navigateTo} ambassador={ambassador} />;
      case 'embajador':
        return <Embajador onNavigate={navigateTo} ambassador={ambassador} />;
      case 'catalogo':
        return <Catalogo initialLinea={catalogoLinea} initialProducto={catalogoProducto} ambassador={ambassador} />;
      case 'tratamientos':
        return <Tratamientos onNavigate={navigateTo} initialTratamiento={tratamientoSeleccionado} />;
      default:
        return <Inicio onNavigate={navigateTo} ambassador={ambassador} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9f5]">
      <Header 
        onSearchClick={() => setIsSearchOpen(true)} 
        currentPage={currentPage}
        onNavigate={navigateTo}
        ambassador={ambassador}
      />
      
      <main>
        {renderPage()}
      </main>

      <Footer onNavigate={navigateTo} ambassador={ambassador} />

      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onSelectProduct={handleSelectProduct}
        onSelectTratamiento={handleSelectTratamiento}
      />

      <Chatbot 
        embajadorName={ambassador?.nombre_completo || leader?.nombre_completo || "Octavio Esparza Juárez"} 
        ambassador={ambassador}
      />
    </div>
  );
}

export default App;
